import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
    ArrowLeft,
    Users,
    Search,
    Plus,
    Edit,
    Trash2,
    X,
    Save,
    Building,
    Mail,
    Phone,
    MapPin,
    FileText,
    AlertCircle,
    Briefcase
} from 'lucide-react';

export default function ClientsManagement() {
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [saving, setSaving] = useState(false);
    
    // Delete confirmation
    const [deleteTarget, setDeleteTarget] = useState(null);
    
    // Form fields
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        email: '',
        phone: '',
        vat_number: '',
        fiscal_code: '',
        notes: ''
    });

    useEffect(() => {
        loadClients();
    }, []);

    const loadClients = async () => {
        try {
            setLoading(true);
            const response = await clientsApi.list();
            setClients(response.data);
        } catch (err) {
            setError('Errore nel caricamento dei clienti');
        } finally {
            setLoading(false);
        }
    };

    const filteredClients = clients.filter(client =>
        client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.vat_number?.includes(searchQuery) ||
        client.fiscal_code?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const openCreateModal = () => {
        setEditingClient(null);
        setFormData({
            name: '',
            address: '',
            email: '',
            phone: '',
            vat_number: '',
            fiscal_code: '',
            notes: ''
        });
        setShowModal(true);
        setError('');
    };

    const openEditModal = (client) => {
        setEditingClient(client);
        setFormData({
            name: client.name,
            address: client.address || '',
            email: client.email || '',
            phone: client.phone || '',
            vat_number: client.vat_number || '',
            fiscal_code: client.fiscal_code || '',
            notes: client.notes || ''
        });
        setShowModal(true);
        setError('');
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingClient(null);
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name.trim()) {
            setError('Il nome è obbligatorio');
            return;
        }

        try {
            setSaving(true);
            setError('');

            if (editingClient) {
                await clientsApi.update(editingClient.id, formData);
                setSuccess('Cliente aggiornato');
            } else {
                await clientsApi.create(formData);
                setSuccess('Cliente creato');
            }

            closeModal();
            loadClients();
            
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nel salvataggio');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;

        try {
            await clientsApi.delete(deleteTarget.id);
            setSuccess('Cliente eliminato');
            setDeleteTarget(null);
            loadClients();
            
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nell\'eliminazione');
            setDeleteTarget(null);
        }
    };

    const updateFormField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div className="min-h-screen bg-black p-4 md:p-8">
            {/* Header */}
            <div className="max-w-6xl mx-auto">
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            onClick={() => navigate('/admin')}
                            className="text-white/60 hover:text-white"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <div>
                            <h1 className="font-heading text-2xl md:text-3xl text-white flex items-center gap-3">
                                <Users className="w-8 h-8 text-[#00FF41]" />
                                Gestione Clienti
                            </h1>
                            <p className="text-white/50 text-sm mt-1">
                                {clients.length} clienti registrati
                            </p>
                        </div>
                    </div>
                    <Button
                        onClick={openCreateModal}
                        className="bg-[#00FF41] text-black hover:bg-[#00CC33] rounded-none"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        NUOVO CLIENTE
                    </Button>
                </div>

                {/* Messages */}
                {error && (
                    <div className="bg-red-500/10 border border-red-500 p-3 mb-4 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-red-500" />
                        <span className="text-red-500">{error}</span>
                        <button onClick={() => setError('')} className="ml-auto text-red-500">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {success && (
                    <div className="bg-green-500/10 border border-green-500 p-3 mb-4">
                        <span className="text-green-500">{success}</span>
                    </div>
                )}

                {/* Search */}
                <div className="mb-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <Input
                            data-testid="clients-page-search-input"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Cerca per nome, email, P.IVA o codice fiscale..."
                            className="bg-[#0A0A0A] border-white/20 text-white pl-10 h-12 rounded-none focus:border-[#00FF41]"
                        />
                    </div>
                </div>

                {/* Clients List */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="w-8 h-8 border-2 border-[#00FF41] border-t-transparent rounded-full animate-spin mx-auto"></div>
                        <p className="text-white/50 mt-4">Caricamento...</p>
                    </div>
                ) : filteredClients.length === 0 ? (
                    <div className="text-center py-12 bg-[#0A0A0A] border border-white/10">
                        <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                        <p className="text-white/50">
                            {searchQuery ? 'Nessun cliente trovato' : 'Nessun cliente registrato'}
                        </p>
                        {!searchQuery && (
                            <Button
                                onClick={openCreateModal}
                                className="mt-4 bg-[#00FF41] text-black"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Crea il primo cliente
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {filteredClients.map(client => (
                            <div
                                key={client.id}
                                className="bg-[#0A0A0A] border border-white/10 p-4 hover:border-[#00FF41]/30 transition-colors"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-10 h-10 bg-[#00FF41]/20 border border-[#00FF41]/50 flex items-center justify-center">
                                                <Building className="w-5 h-5 text-[#00FF41]" />
                                            </div>
                                            <div>
                                                <h3 className="text-white font-medium text-lg">
                                                    {client.name}
                                                </h3>
                                                {client.cases_count > 0 && (
                                                    <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5">
                                                        {client.cases_count} caso/i
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                                            {client.email && (
                                                <div className="flex items-center gap-2 text-white/60 text-sm">
                                                    <Mail className="w-4 h-4 text-white/40" />
                                                    {client.email}
                                                </div>
                                            )}
                                            {client.phone && (
                                                <div className="flex items-center gap-2 text-white/60 text-sm">
                                                    <Phone className="w-4 h-4 text-white/40" />
                                                    {client.phone}
                                                </div>
                                            )}
                                            {client.address && (
                                                <div className="flex items-center gap-2 text-white/60 text-sm">
                                                    <MapPin className="w-4 h-4 text-white/40" />
                                                    {client.address}
                                                </div>
                                            )}
                                            {client.vat_number && (
                                                <div className="flex items-center gap-2 text-white/60 text-sm">
                                                    <FileText className="w-4 h-4 text-white/40" />
                                                    P.IVA: {client.vat_number}
                                                </div>
                                            )}
                                            {client.fiscal_code && (
                                                <div className="flex items-center gap-2 text-white/60 text-sm">
                                                    <Briefcase className="w-4 h-4 text-white/40" />
                                                    CF: {client.fiscal_code}
                                                </div>
                                            )}
                                        </div>
                                        
                                        {client.notes && (
                                            <p className="text-white/40 text-sm mt-2 italic">
                                                "{client.notes}"
                                            </p>
                                        )}
                                    </div>
                                    
                                    <div className="flex items-center gap-2 ml-4">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => openEditModal(client)}
                                            className="text-[#00FF41] hover:bg-[#00FF41]/10 border border-[#00FF41]/30"
                                        >
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setDeleteTarget(client)}
                                            disabled={client.cases_count > 0}
                                            className="text-red-500 hover:bg-red-500/10 border border-red-500/30 disabled:opacity-30 disabled:cursor-not-allowed"
                                            title={client.cases_count > 0 ? 'Non puoi eliminare un cliente con casi associati' : 'Elimina cliente'}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-[#0A0A0A] border border-white/20 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-4 border-b border-white/10">
                            <h2 className="font-heading text-xl text-white flex items-center gap-2">
                                <Building className="w-5 h-5 text-[#00FF41]" />
                                {editingClient ? 'Modifica Cliente' : 'Nuovo Cliente'}
                            </h2>
                            <button
                                onClick={closeModal}
                                className="text-white/60 hover:text-white"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <form onSubmit={handleSubmit} className="p-4 space-y-4">
                            {error && (
                                <div className="bg-red-500/10 border border-red-500 p-3 flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-500" />
                                    <span className="text-red-500 text-sm">{error}</span>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <Label className="text-white/60 uppercase text-xs">Nome / Ragione Sociale *</Label>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => updateFormField('name', e.target.value)}
                                        placeholder="Nome cliente o azienda"
                                        required
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>

                                <div>
                                    <Label className="text-white/60 uppercase text-xs">Email</Label>
                                    <Input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => updateFormField('email', e.target.value)}
                                        placeholder="email@esempio.it"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>

                                <div>
                                    <Label className="text-white/60 uppercase text-xs">Telefono</Label>
                                    <Input
                                        value={formData.phone}
                                        onChange={(e) => updateFormField('phone', e.target.value)}
                                        placeholder="+39 ..."
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <Label className="text-white/60 uppercase text-xs">Indirizzo</Label>
                                    <Input
                                        value={formData.address}
                                        onChange={(e) => updateFormField('address', e.target.value)}
                                        placeholder="Via, CAP, Città"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>

                                <div>
                                    <Label className="text-white/60 uppercase text-xs">Partita IVA</Label>
                                    <Input
                                        value={formData.vat_number}
                                        onChange={(e) => updateFormField('vat_number', e.target.value)}
                                        placeholder="12345678901"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>

                                <div>
                                    <Label className="text-white/60 uppercase text-xs">Codice Fiscale</Label>
                                    <Input
                                        value={formData.fiscal_code}
                                        onChange={(e) => updateFormField('fiscal_code', e.target.value.toUpperCase())}
                                        placeholder="RSSMRA80A01H501U"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1 uppercase"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <Label className="text-white/60 uppercase text-xs">Note</Label>
                                    <Textarea
                                        value={formData.notes}
                                        onChange={(e) => updateFormField('notes', e.target.value)}
                                        placeholder="Note aggiuntive sul cliente..."
                                        rows={3}
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1 resize-none"
                                    />
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={closeModal}
                                    className="text-white/60"
                                >
                                    Annulla
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={saving}
                                    className="bg-[#00FF41] text-black hover:bg-[#00CC33]"
                                >
                                    <Save className="w-4 h-4 mr-2" />
                                    {saving ? 'Salvataggio...' : (editingClient ? 'AGGIORNA' : 'CREA CLIENTE')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                <AlertDialogContent className="bg-[#0A0A0A] border border-white/20">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Conferma Eliminazione</AlertDialogTitle>
                        <AlertDialogDescription className="text-white/60">
                            Sei sicuro di voler eliminare il cliente <strong className="text-white">"{deleteTarget?.name}"</strong>?
                            <br />
                            Questa azione non può essere annullata.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/10">
                            Annulla
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-red-500 text-white hover:bg-red-600"
                        >
                            Elimina
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
